import type { EngagementChatAttachment } from '@houzy/schemas/partner-cockpit/chat';
import type {
    PartnerCockpitContactDetail,
    PartnerCockpitLeadStatus,
    PartnerCockpitOfferRequestContact,
} from '@houzy/schemas/partner-cockpit/offer-requests';
import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import {
    useChatMessages,
    useChatPolling,
    usePostChatMessage,
    useRemoveChatAttachment,
    useUploadChatAttachment,
} from '../api/useChat';
import {
    engagementChatAttachmentValidationMessageKey,
    engagementChatAttachmentValidationMessageParams,
    matchAcceptedAttachmentFiles,
    validateEngagementChatAttachmentFiles,
} from '../utils/chatAttachmentValidation';
import { OfferRequestChatCard } from './OfferRequestChatCard';

export type OfferRequestChatProps = {
    engagementUuid: string;
    offerRequestId: string;
    contact?: PartnerCockpitOfferRequestContact;
    leadStatus: PartnerCockpitLeadStatus;
    contactRestricted: boolean;
    onRevealContactDetail: (detail: PartnerCockpitContactDetail) => void;
    isInputDisabled?: boolean;
    isOnOldContract?: boolean;
    fillHeight?: boolean;
};

export function OfferRequestChat({
    engagementUuid,
    offerRequestId,
    contact,
    leadStatus,
    contactRestricted,
    onRevealContactDetail,
    isInputDisabled,
    isOnOldContract,
    fillHeight = false,
}: OfferRequestChatProps): ReactElement {
    const { t } = useTranslation();
    const messagesQuery = useChatMessages(engagementUuid);
    const postMessage = usePostChatMessage(engagementUuid, offerRequestId);
    const uploadAttachment = useUploadChatAttachment(engagementUuid);
    const removeAttachment = useRemoveChatAttachment(engagementUuid);
    const [pendingAttachments, setPendingAttachments] = useState<EngagementChatAttachment[]>([]);
    const [uploadingCount, setUploadingCount] = useState(0);
    const [uploadError, setUploadError] = useState<string | null>(null);
    useChatPolling(engagementUuid, messagesQuery.messages.at(-1)?.uuid);
    const isChatClosed = !messagesQuery.canSendMessages;

    return (
        <OfferRequestChatCard
            {...(contact === undefined ? {} : { contact })}
            leadStatus={leadStatus}
            contactRestricted={contactRestricted}
            onRevealContactDetail={onRevealContactDetail}
            messages={messagesQuery.messages}
            isLoading={messagesQuery.isPending}
            isLoadError={messagesQuery.isError}
            isSending={postMessage.isPending || uploadingCount > 0}
            isSendError={postMessage.isError}
            isUploadingAttachments={uploadingCount > 0}
            uploadError={uploadError}
            onDismissUploadError={() => setUploadError(null)}
            hasOlderMessages={messagesQuery.hasNextPage}
            isLoadingOlderMessages={messagesQuery.isFetchingNextPage}
            onLoadOlderMessages={() => void messagesQuery.fetchNextPage()}
            pendingAttachments={pendingAttachments}
            onUploadFiles={(files) => {
                const { accepted, rejections } = validateEngagementChatAttachmentFiles(
                    files.map((file) => ({
                        name: file.name,
                        type: file.type,
                        size: file.size,
                    })),
                    { pendingCount: pendingAttachments.length },
                );
                if (rejections.length > 0) {
                    const firstRejection = rejections[0];
                    if (firstRejection !== undefined) {
                        setUploadError(
                            t(
                                engagementChatAttachmentValidationMessageKey(firstRejection.failure),
                                engagementChatAttachmentValidationMessageParams(firstRejection.failure),
                            ),
                        );
                    }
                } else {
                    setUploadError(null);
                }

                const filesToUpload = matchAcceptedAttachmentFiles(files, accepted);
                if (filesToUpload.length === 0) {
                    return;
                }

                void (async (): Promise<void> => {
                    for (const file of filesToUpload) {
                        setUploadingCount((count) => count + 1);
                        try {
                            const attachment = await uploadAttachment.mutateAsync(file);
                            setPendingAttachments((current) => [...current, attachment]);
                            setUploadError(null);
                        } catch (error) {
                            const message = error instanceof Error ? error.message : undefined;
                            setUploadError(
                                message && message.length > 0 ? message : t('OFFER_REQUESTS.CHAT.ERROR.UPLOAD'),
                            );
                        } finally {
                            setUploadingCount((count) => count - 1);
                        }
                    }
                })();
            }}
            onRemoveAttachment={(attachmentUuid) => {
                removeAttachment.mutate(attachmentUuid, {
                    onSuccess: () => {
                        setPendingAttachments((current) =>
                            current.filter((attachment) => attachment.uuid !== attachmentUuid),
                        );
                    },
                });
            }}
            onSendMessage={async (input) => {
                await postMessage.mutateAsync(input);
                setPendingAttachments([]);
            }}
            isInputDisabled={isInputDisabled === true || isChatClosed}
            {...(isChatClosed ? { disabledNotice: t('OFFER_REQUESTS.CHAT.CLOSED') } : {})}
            {...(isOnOldContract === undefined ? {} : { isOnOldContract })}
            fillHeight={fillHeight}
        />
    );
}
